import DebugJson from 'features/debug/components/DebugJson'
import DebugRow from 'features/debug/components/DebugRow'
import DebugSection from 'features/debug/components/DebugSection'
import CollapsibleSection from '@ui/elements/CollapsibleSection'
import useOrderStep from 'features/order/hooks/useOrderStep'
import { ItemValidationTrace, validateItem } from '@order/utils/validateItem'
import { PriceValue } from '@ca/common/types'
import { ScrollView, View } from 'react-native'
import Typography from '@ui/typography'
import { StepCtx } from '@order/steps/useStepCtx'
import { Format, RegionCode } from '@ca/common'

type Step = NonNullable<ReturnType<typeof useOrderStep>>

export interface StepDebugData {
  step: Step
  ctx: StepCtx
}

type BadgeVariant =
  | 'matched'
  | 'skipped'
  | 'requirement'
  | 'condition'
  | 'no variant'

const BADGE_STYLES: Record<BadgeVariant, { bg: string; text: string }> = {
  matched: { bg: 'bg-green-100', text: 'text-green-600' },
  skipped: { bg: 'bg-black/5', text: 'opacity-40' },
  requirement: { bg: 'bg-red-100', text: 'text-red-500' },
  condition: { bg: 'bg-red-100', text: 'text-red-500' },
  'no variant': { bg: 'bg-orange-100', text: 'text-orange-500' },
}

function Badge({ variant }: { variant: BadgeVariant }) {
  const { bg, text } = BADGE_STYLES[variant]
  return (
    <View className={`${bg} rounded-md px-1.5 py-0.5`}>
      <Typography variant="XSB" className={text}>
        {variant}
      </Typography>
    </View>
  )
}

// ─── Excluded items ───────────────────────────────────────────────────────────

type ExcludedItem =
  | {
      id: string | number
      name: string
      reason: 'base'
      data: unknown
      trace: ItemValidationTrace
    }
  | {
      id: string | number
      name: string
      reason: 'no-variant'
      data: unknown
      variantTraces: VariantEntry[]
    }

function ExcludedItemRow({
  item,
  regionCode,
}: {
  item: ExcludedItem
  regionCode?: RegionCode
}) {
  const badge: BadgeVariant =
    item.reason === 'no-variant'
      ? 'no variant'
      : item.trace.failedRequirement
      ? 'requirement'
      : 'condition'

  return (
    <CollapsibleSection
      header={
        <>
          <Typography variant="XSB">{`${item.name}  ·  ${item.id}`}</Typography>
          <Badge variant={badge} />
        </>
      }
    >
      <View className="gap-3 pb-4">
        <View className="px-6">
          <DebugJson value={item.data} initialOpen />
        </View>
        {item.reason === 'no-variant' ? (
          <DebugSection title="Variants">
            {item.variantTraces.map((v, i) => (
              <VariantRow key={i} variant={v} regionCode={regionCode} />
            ))}
          </DebugSection>
        ) : (
          <DebugSection title="Trace">
            <DebugJson
              value={
                item.trace.failedRequirement ??
                item.trace.condition?.failurePath
              }
              initialOpen
            />
          </DebugSection>
        )}
      </View>
    </CollapsibleSection>
  )
}

function ExcludedItemList({
  label,
  items,
  regionCode,
}: {
  label: string
  regionCode?: RegionCode
  items: ExcludedItem[]
}) {
  return (
    <DebugSection title={label}>
      <View className="gap-3">
        {items.map((item) => (
          <ExcludedItemRow
            regionCode={regionCode}
            key={String(item.id)}
            item={item}
          />
        ))}
      </View>
    </DebugSection>
  )
}

// ─── Matched items ────────────────────────────────────────────────────────────

type VariantEntry =
  | {
      status: 'matched' | 'skipped'
      value: string
      condition: string | null
      price: number
    }
  | {
      status: 'failed'
      value: string
      condition: string | null
      price: number
      details: unknown
      trace: ItemValidationTrace
    }

function VariantRow({
  variant,
  regionCode,
}: {
  variant: VariantEntry
  regionCode?: RegionCode
}) {
  const priceLabel = variant.price
    ? `  ·  ${Format.region.priceCompact(variant.price, regionCode ?? 'de')}`
    : '  ·  included'
  const label = `${variant.value}${priceLabel}`

  if (variant.status !== 'failed') {
    return (
      <CollapsibleSection
        header={
          <>
            <Typography variant="XSB" className="flex-1">
              {label}
            </Typography>
            <Badge variant={variant.status} />
          </>
        }
      >
        <View className="gap-3 px-6 pb-3">
          <DebugJson value={variant} initialOpen />
        </View>
      </CollapsibleSection>
    )
  }

  const badge: BadgeVariant = variant.trace.failedRequirement
    ? 'requirement'
    : 'condition'
  const traceData =
    variant.trace.failedRequirement ?? variant.trace.condition?.failurePath

  return (
    <CollapsibleSection
      header={
        <>
          <Typography variant="XSB" className="flex-1">
            {label}
          </Typography>
          <Badge variant={badge} />
        </>
      }
    >
      <View className="gap-3 pb-3">
        <View className="gap-1 px-6">
          <DebugJson value={variant.details} initialOpen />
        </View>
        <DebugSection title="Trace">
          <DebugJson value={traceData} initialOpen />
        </DebugSection>
      </View>
    </CollapsibleSection>
  )
}

function MatchedItemRow({
  item,
  regionCode,
}: {
  regionCode?: RegionCode
  item: {
    id: string | number
    name: string
    variable: string
    active: {
      value: string
      title: string
      recommended: boolean
      confirm: unknown
      isBase: boolean
    }
    base: { value: string; condition: string | null }
    variants: VariantEntry[]
  }
}) {
  return (
    <CollapsibleSection
      header={
        <Typography variant="XSB">{`${item.name}  ·  ${item.id}`}</Typography>
      }
    >
      <View className="gap-3 pb-4">
        <View className="gap-1 px-6">
          <DebugJson
            value={{
              variable: item.variable,
              active: item.active,
              base: item.base,
            }}
            initialOpen
          />
        </View>
        {item.variants.length > 0 && (
          <DebugSection title="Variants">
            {item.variants.map((v, i) => (
              <VariantRow key={i} variant={v} regionCode={regionCode} />
            ))}
          </DebugSection>
        )}
      </View>
    </CollapsibleSection>
  )
}

function MatchedItemList({
  label,
  items,
  regionCode,
}: {
  label: string
  regionCode?: RegionCode
  items: Array<Parameters<typeof MatchedItemRow>[0]['item']>
}) {
  return (
    <DebugSection title={label}>
      <View className="gap-3">
        {items.map((item) => (
          <MatchedItemRow
            key={String(item.id)}
            regionCode={regionCode}
            item={item}
          />
        ))}
      </View>
    </DebugSection>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const variantSummary = (v: { value: string; condition: string | null }) => ({
  value: v.value,
  condition: v.condition,
})

// ─── View ─────────────────────────────────────────────────────────────────────

export default function StepDebugView({ step, ctx }: StepDebugData) {
  const stepId = step.id
  const { config } = ctx

  const regionCode = config?.region?.countryCode

  const stepMeta = {
    id: step.id,
    name: step.name,
    variable: step.variable,
    template: step.template,
    title: step.title,
    itemNextStep: step.itemNextStep,
  }

  const matchedItemIds = new Set(step.items.map((i) => i.id))

  const excludedItems: ExcludedItem[] =
    config?.steps[stepId]?.items
      .map((id) => config.items[id])
      .filter((raw) => raw && !matchedItemIds.has(raw.id))
      .map((raw) => {
        const conditions = config?.conditions ?? {}
        const refraction = ctx.refraction ?? undefined
        const baseTrace = validateItem(
          raw.base,
          ctx.values,
          conditions,
          refraction
        )
        const data = {
          variable: raw.variable,
          base: variantSummary(raw.base),
        }

        if (baseTrace.result && raw.requireVariant) {
          const variantTraces: VariantEntry[] = raw.variants.map((v) => {
            const { condition, value, price, ...details } = v
            return {
              status: 'failed' as const,
              value,
              condition,
              price: PriceValue.safe(price, config?.region),
              details,
              trace: validateItem(v, ctx.values, conditions, refraction),
            }
          })
          return {
            id: raw.id,
            name: raw.name,
            reason: 'no-variant' as const,
            data,
            variantTraces,
          }
        }

        return {
          id: raw.id,
          name: raw.name,
          reason: 'base' as const,
          data,
          trace: baseTrace,
        }
      }) ?? []

  const items = step.items.map((item) => {
    const activeValue = item.active.value
    const matchedIndex = item.variants.findIndex((v) => v.value === activeValue)

    const conditions = config?.conditions ?? {}
    const refraction = ctx.refraction ?? undefined
    const variants: VariantEntry[] = item.variants.map((v, i) => {
      const price = PriceValue.safe(v.price, config?.region)
      if (matchedIndex !== -1 && i > matchedIndex) {
        return {
          status: 'skipped',
          value: v.value,
          condition: v.condition,
          price,
        }
      }
      if (v.value === activeValue) {
        return {
          status: 'matched',
          value: v.value,
          condition: v.condition,
          price,
        }
      }
      const { condition, value, ...details } = v
      return {
        status: 'failed',
        value,
        condition,
        price,
        details,
        trace: validateItem(v, ctx.values, conditions, refraction),
      }
    })

    return {
      id: item.id,
      name: item.name,
      variable: item.variable,
      active: {
        value: activeValue,
        title: item.active.title,
        recommended: !!item.active.recommended,
        confirm: item.active.confirm ?? null,
        isBase: matchedIndex === -1,
      },
      base: variantSummary(item.base),
      variants,
    }
  })

  const customOptions = step.customOptions.map((opt) => ({
    id: opt.id,
    name: opt.name,
    title: opt.title,
    nextStep: opt.nextStep,
  }))

  return (
    <ScrollView className="flex-1">
      <DebugRow label="Step" value={stepMeta} initialOpen>
        <MatchedItemList
          regionCode={regionCode}
          label="Matched Items"
          items={items}
        />
        {excludedItems.length > 0 && (
          <ExcludedItemList
            regionCode={regionCode}
            label="Excluded Items"
            items={excludedItems}
          />
        )}
        {customOptions.length > 0 && (
          <DebugRow
            label="Custom Options"
            value={customOptions}
            initialOpen={false}
          />
        )}
      </DebugRow>
      <DebugRow label="States" value={step.states} initialOpen={false} />
      <DebugRow label="Values" value={step.values} initialOpen={false} />
    </ScrollView>
  )
}
